import { useRef } from 'react';
import { useStore } from 'react-redux';
import { RootState } from '../store/store';
import { DEFAULT_MAP_CENTER } from './useDeviceLocation';

/** Zoom used the first time a map is ever opened, before anything is remembered. */
export const DEFAULT_MAP_ZOOM = 10;

export interface InitialMapView {
  center: [number, number];
  zoom: number;
  /**
   * True when center/zoom came from the remembered viewport rather than the
   * default. Callers use it to suppress the device-location re-centre: a
   * remembered view is position context, and moving it would undo the very
   * thing being restored (#401, #441).
   */
  remembered: boolean;
}

/**
 * The viewport a map should be constructed with (#441).
 *
 * Read once, at mount, and deliberately not via `useSelector`. The viewport is
 * written on every `moveend`, so subscribing would re-render the whole map
 * component on each pan — for a value that `MapContainer` only reads at
 * construction and can never act on again. `useRef` freezes it for the life of
 * this mount, which is exactly the lifetime that matters: a view switch
 * unmounts the map, and the next mount picks up whatever was last recorded.
 */
export const useInitialMapView = (): InitialMapView => {
  const store = useStore<RootState>();
  const initial = useRef<InitialMapView | null>(null);

  if (initial.current === null) {
    const { center, zoom } = store.getState().mapView;
    initial.current =
      center !== null && zoom !== null
        ? { center, zoom, remembered: true }
        : { center: DEFAULT_MAP_CENTER, zoom: DEFAULT_MAP_ZOOM, remembered: false };
  }

  return initial.current;
};
